import { Flags } from "@/providers/LaunchDarklyProvider";
import { useLocalStorageState } from "ahooks";
import { useFlags } from "launchdarkly-react-client-sdk";

export type AgentReasoningEffort = "low" | "standard" | "high" | "x-high";

const DEFAULT_EFFORT: AgentReasoningEffort = "standard";

/**
 * Persists the agent reasoning-effort selection under `storageKey` and coerces
 * the hidden `x-high` tier back to default when the flag is off. Scope-agnostic
 * so canvas, workspace chat, etc. can each persist their own selection.
 *
 * `defaultEffort` is the tier used until the user picks one (and the tier
 * a hidden `x-high` falls back to); surfaces that should start higher than
 * "standard" pass their own.
 */
export function useReasoningEffort(
  storageKey: string,
  defaultEffort: AgentReasoningEffort = DEFAULT_EFFORT,
) {
  const flags = useFlags<Flags>();
  const [effort, setEffort] = useLocalStorageState<AgentReasoningEffort>(
    storageKey,
    { defaultValue: defaultEffort },
  );

  const resolved = effort ?? defaultEffort;
  // Coerce x-high back to the default when the flag is off so users that
  // previously selected Extreme aren't stuck on a hidden tier.
  const sanitized =
    !flags.extremeReasoningEffort && resolved === "x-high"
      ? defaultEffort
      : resolved;

  return [sanitized, setEffort] as const;
}

export function useCanvasReasoningEffort(canvasId: string | undefined) {
  return useReasoningEffort(
    `canvas_reasoning_effort_${canvasId ?? "__none__"}`,
  );
}
